import { DollarOutlined, EnvironmentOutlined, FileTextOutlined, MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Card, Col, Divider, Row, Space, Switch, Tag, Typography } from 'antd'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import axiosInstance from '../../../variables/api/axiosInstance'

const { Title, Text } = Typography;


// Couleurs de la charte
const PRIMARY_COLOR = '#242166';
const SECONDARY_COLOR = '#1890ff';
const SUCCESS_COLOR = '#52c41a';
const WARNING_COLOR = '#faad14';

const InfoItem = ({ icon, label, value, color = PRIMARY_COLOR }) => (
    <div style={{ marginBottom: '16px' }}>
        <Space>
            <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: `${color}10`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: color
            }}>
                {icon}
            </div>
            <div>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>{label}</Text>
                <Text strong style={{ fontSize: '14px' }}>{value}</Text>
            </div>
        </Space>
    </div>
);

const ProfilingAgence = ({ agence, nbreContrats, montantPrimesMensuelles, logo, categories = [], commissionValues = {}, onManageRates, canManageRates = false, loadingRates = false }) => {
    // Base de commission : false = prime nette seule (défaut), true = prime nette + accessoires.
    const [commAccessoires, setCommAccessoires] = useState(!!agence?.commissionAvecAccessoires);
    const [savingComm, setSavingComm] = useState(false);
    useEffect(() => {
        setCommAccessoires(!!agence?.commissionAvecAccessoires);
    }, [agence]);

    const saveCommissionBase = (checked) => {
        if (!agence?.codeId) return;
        setSavingComm(true);
        setCommAccessoires(checked);
        axiosInstance
            .put(`/api/agence/${agence.codeId}/commission-accessoires/${checked}`)
            .then(() => toast.success("Base de commission mise à jour"))
            .catch(() => { toast.error("Erreur lors de la mise à jour"); setCommAccessoires(!checked); })
            .finally(() => setSavingComm(false));
    };

    const tauxList = categories.map((cat) => ({
        code: cat.codeCategorie,
        nom: cat.nom,
        taux: commissionValues?.[cat.codeCategorie],
    })).filter((item) => item.taux !== undefined);

    return (
        <Col xs={24} lg={8}>
            <Card
                style={{
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(36, 33, 102, 0.08)',
                    border: 'none'
                }}
                bodyStyle={{ padding: '32px' }}
            >
                {/* Avatar et informations principales */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    {logo ? (
                        <div
                            style={{
                                display: 'inline-flex',
                                marginBottom: '16px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                maxWidth: 220,
                                maxHeight: 140
                            }}
                        >
                            <img
                                src={logo}
                                alt="Logo agence"
                                style={{
                                    width: 'auto',
                                    height: 'auto',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    ) : (
                        <Avatar
                            size={120}
                            style={{
                                marginBottom: '16px',
                                boxShadow: '0 4px 12px rgba(36, 33, 102, 0.15)',
                                border: `4px solid ${PRIMARY_COLOR}15`,
                                backgroundColor: PRIMARY_COLOR
                            }}
                        >
                            {agence?.nom?.charAt(0) || agence?.organisme?.raisonSociale?.charAt(0)}
                        </Avatar>
                    )}
                    <Title level={4} style={{ margin: '8px 0', color: PRIMARY_COLOR }}>
                        {agence?.nom}
                    </Title>
                    <Text type="secondary">{agence?.organisme?.raisonSociale}</Text>
                    <br />
                    <Tag
                        color={PRIMARY_COLOR}
                        style={{
                            marginTop: '12px',
                            borderRadius: '12px',
                            padding: '4px 16px',
                            fontWeight: 500
                        }}
                    >
                        Code #{agence?.codeAgence}
                    </Tag>
                </div>

                <Divider style={{ margin: '24px 0' }}>
                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                        Statistiques Rapides
                    </Text>
                </Divider>

                {/* Statistiques rapides */}
                <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
                    <Col span={12}>
                        <div style={{
                            textAlign: 'center',
                            padding: '16px',
                            borderRadius: '12px',
                            backgroundColor: `${PRIMARY_COLOR}08`
                        }}>
                            <FileTextOutlined style={{ fontSize: '24px', color: PRIMARY_COLOR }} />
                            <Title level={3} style={{ margin: '8px 0', color: PRIMARY_COLOR }}>
                                {nbreContrats}
                            </Title>
                            <Text type="secondary" style={{ fontSize: '12px' }}>Contrats</Text>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{
                            textAlign: 'center',
                            padding: '16px',
                            borderRadius: '12px',
                            backgroundColor: `${SUCCESS_COLOR}08`
                        }}>
                            <DollarOutlined style={{ fontSize: '24px', color: SUCCESS_COLOR }} />
                            <Title level={3} style={{ margin: '8px 0', color: SUCCESS_COLOR }}>
                                {(montantPrimesMensuelles / 1000000).toFixed(1)}M
                            </Title>
                            <Text type="secondary" style={{ fontSize: '12px' }}>Production mois</Text>
                        </div>
                    </Col>
                </Row>

                <Divider style={{ margin: '24px 0' }}>
                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                        Détails de l'agence
                    </Text>
                </Divider>

                {/* Détails */}
                <div style={{ marginTop: '24px' }}>
                    <InfoItem
                        icon={<UserOutlined />}
                        label="Responsable"
                        value={agence?.responsable}
                        color={PRIMARY_COLOR}
                    />
                    <InfoItem
                        icon={<MailOutlined />}
                        label="Email"
                        value={agence?.email}
                        color={SECONDARY_COLOR}
                    />
                    <InfoItem
                        icon={<PhoneOutlined />}
                        label="Contact(s)"
                        value={(agence?.telephone1Agence ? agence?.telephone1Agence : 'N/A') + (agence?.telephone2Agence ? ` / ${agence?.telephone2Agence}` : '')}
                        color={SUCCESS_COLOR}
                    />
                    <InfoItem
                        icon={<EnvironmentOutlined />}
                        label="Ville / Adresse"
                        value={agence?.ville}
                        color={WARNING_COLOR}
                    />

                    <Divider style={{ margin: '16px 0' }}>
                        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                            Taux de commission
                        </Text>
                    </Divider>
                    <div style={{ marginBottom: 8 }}>
                        {tauxList.length ? (
                            <Space wrap>
                                {tauxList.map((tc) => (
                                    <Tag
                                        key={tc.code}
                                        color={PRIMARY_COLOR}
                                        style={{
                                            borderRadius: '10px',
                                            padding: '6px 10px',
                                            backgroundColor: `${PRIMARY_COLOR}12`,
                                            color: PRIMARY_COLOR
                                        }}
                                    >
                                        {tc.nom} : {tc.taux || 0}%
                                    </Tag>
                                ))}
                            </Space>
                        ) : (
                            <Text type="secondary">Aucun taux défini</Text>
                        )}
                    </div>
                    {/* Base de calcul de la commission de cette agence. */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 12 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Base de commission : <b>{commAccessoires ? 'Prime nette + accessoires' : 'Prime nette seule'}</b>
                        </Text>
                        <Switch checked={commAccessoires} loading={savingComm} disabled={!canManageRates}
                            onChange={saveCommissionBase} />
                    </div>
                    <Button
                        type="primary"
                        block
                        icon={<DollarOutlined />}
                        onClick={onManageRates}
                        disabled={!canManageRates || loadingRates}
                        loading={loadingRates}
                        style={{
                            marginTop: 12,
                            backgroundColor: canManageRates ? PRIMARY_COLOR : '#d9d9d9',
                            borderColor: canManageRates ? PRIMARY_COLOR : '#d9d9d9',
                            borderRadius: '10px',
                            height: '42px',
                            fontWeight: 600
                        }}
                    >
                        Gérer les taux de commission
                    </Button>
                </div>
            </Card>
        </Col>
    )
}

export default ProfilingAgence
